import React, { type FC, memo } from "react";
import { Sparkles } from "@gravity-ui/icons";

import type { AgentRun } from "./useAgentRun";
import { useTranslation } from "@domain/intl/useTranslation";
import { ProductColors } from "@ui-components/ProductColors";

interface Props {
    run: AgentRun;
    onRestore(): void;
}

const AgentRunPillImpl: FC<Props> = ({ run, onRestore }) => {
    const { t } = useTranslation();
    const { state, stepNumber, stepTotal, stepStates, stepIds, askingNow, messages } = run;

    // Only the latest log entry tells what runs now; an older progress line is stale.
    const progress = messages.at(-1)?.progress;
    const status =
        state === "finished"
            ? t("app.agent.finished")
            : askingNow
              ? t("app.agent.pill.waiting")
              : state === "paused"
                ? t("app.agent.stepList.paused")
                : state === "pausing"
                  ? t("app.agent.stepList.pausing")
                  : undefined;
    const parts = [
        t("app.agent.title"),
        ...(stepNumber > 0 ? [t("app.agent.pill.step", { step: stepNumber, total: stepTotal })] : []),
        ...(progress && state === "running" ? [t("app.agent.pill.progress", progress)] : []),
        ...(status ? [status] : []),
    ];
    const finishedSteps = stepIds.filter(
        (id) => !["pending", "running", "asking", "pausing", "paused"].includes(stepStates[id].state),
    );
    const share = stepTotal > 0 ? Math.round((finishedSteps.length / stepTotal) * 100) : 0;

    return (
        <button
            type="button"
            data-testid="agent-run-pill"
            onClick={onRestore}
            className={`fixed bottom-6 right-6 z-50 flex min-w-64 flex-col gap-1.5 rounded-2xl px-4 py-2.5 text-left text-sm text-white shadow-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent ${ProductColors.AIGradient.className}`}
        >
            <span className="flex items-center gap-2">
                <Sparkles aria-hidden className="size-4 shrink-0" />
                <span>{parts.join(" · ")}</span>
            </span>
            <span aria-hidden className="h-1 overflow-hidden rounded-sm bg-white/30">
                <span className="block h-full bg-white" style={{ width: `${share}%` }} />
            </span>
        </button>
    );
};

export const AgentRunPill = memo(AgentRunPillImpl);
